import * as XLSX from 'xlsx';
import * as XLSXStyle from 'xlsx-js-style';
import * as FileSaver from 'file-saver';
import moment from 'moment';

/**
 * Export Utilities for Admin Reports
 * Handles Excel generation with various formatting options
 */

/**
 * Basic Excel export without styling
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {Object} options - Export options
 * @param {string} options.sheetName - Sheet name (default: 'Sheet1')
 * @param {Array} options.columnOrder - Array of column keys in desired order
 * @param {Object} options.columnHeaders - Map of key to display name
 */
export function exportToExcel(data, filename, options = {}) {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    try {
        const {
            sheetName = 'Sheet1',
            columnOrder = null,
            columnHeaders = null
        } = options;

        // Transform data if column order or headers specified
        let transformedData = data;
        if (columnOrder || columnHeaders) {
            transformedData = data.map(row => {
                const newRow = {};
                const keys = columnOrder || Object.keys(row);
                keys.forEach(key => {
                    const header = columnHeaders?.[key] || key;
                    newRow[header] = row[key];
                });
                return newRow;
            });
        }

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(transformedData);

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);

        // Generate buffer
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

        // Save file
        const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        });
        
        const timestamp = moment(new Date()).format("yyyyMMDDHHmmss");
        FileSaver.saveAs(blob, `${filename}_${timestamp}.xlsx`);
    } catch (error) {
        console.error('Error exporting to Excel:', error);
        throw error;
    }
}

/**
 * Export with styled headers and alternating row colors
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {Object} styling - Styling options
 * @param {string} styling.headerColor - Header background color (hex without #)
 * @param {string} styling.oddRowColor - Odd row background color (hex without #)
 * @param {string} styling.evenRowColor - Even row background color (hex without #)
 * @param {string} styling.sheetName - Sheet name
 */
export function exportToStyledExcel(data, filename, styling = {}) {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    try {
        const {
            headerColor = '4F81BD',
            oddRowColor = 'DBE5F1',
            evenRowColor = 'B8CCE4',
            sheetName = 'Report'
        } = styling;

        // Create worksheet
        const ws = XLSXStyle.utils.json_to_sheet(data);

        // Get range
        const range = XLSXStyle.utils.decode_range(ws['!ref']);

        // Apply styling
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cell_address = { c: C, r: R };
                const cell_ref = XLSXStyle.utils.encode_cell(cell_address);
                if (!ws[cell_ref]) continue;

                // Header style
                if (R === 0) {
                    ws[cell_ref].s = {
                        fill: { patternType: "solid", fgColor: { rgb: headerColor } },
                        font: { bold: true, color: { rgb: "FFFFFF" } },
                        alignment: { horizontal: "center", vertical: "center" }
                    };
                }
                // Odd rows
                else if (R % 2 === 1) {
                    ws[cell_ref].s = {
                        fill: { patternType: "solid", fgColor: { rgb: oddRowColor } }
                    };
                }
                // Even rows
                else {
                    ws[cell_ref].s = {
                        fill: { patternType: "solid", fgColor: { rgb: evenRowColor } }
                    };
                }
            }
        }

        // Create workbook
        const wb = XLSXStyle.utils.book_new();
        XLSXStyle.utils.book_append_sheet(wb, ws, sheetName);

        // Generate buffer
        const wbout = XLSXStyle.write(wb, { bookType: 'xlsx', type: 'binary' });

        // Convert to array buffer
        const s2ab = s => {
            const buf = new ArrayBuffer(s.length);
            const view = new Uint8Array(buf);
            for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
            return buf;
        };

        const blob = new Blob([s2ab(wbout)], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const timestamp = moment(new Date()).format("yyyyMMDDHHmmss");
        FileSaver.saveAs(blob, `${filename}_${timestamp}.xlsx`);
    } catch (error) {
        console.error('Error exporting styled Excel:', error);
        throw error;
    }
}

/**
 * Export multiple sheets to one workbook
 * @param {Array} sheets - Array of sheet objects
 * @param {string} sheets[].name - Sheet name
 * @param {Array} sheets[].data - Sheet data
 * @param {string} filename - Workbook filename
 */
export function exportMultiSheet(sheets, filename) {
    if (!sheets || sheets.length === 0) {
        console.warn('No sheets to export');
        return;
    }

    try {
        const wb = XLSX.utils.book_new();

        sheets.forEach(sheet => {
            if (sheet.data && sheet.data.length > 0) {
                const ws = XLSX.utils.json_to_sheet(sheet.data);
                XLSX.utils.book_append_sheet(wb, ws, sheet.name || 'Sheet');
            }
        });

        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        });

        const timestamp = moment(new Date()).format("yyyyMMDDHHmmss");
        FileSaver.saveAs(blob, `${filename}_${timestamp}.xlsx`);
    } catch (error) {
        console.error('Error exporting multi-sheet Excel:', error);
        throw error;
    }
}

/**
 * Format raw API data for Excel export
 * @param {Array} rawData - Raw API response data
 * @param {Object} columnMap - Map of API keys to Excel column names
 * @param {Function} transformRow - Optional row transformation function
 */
export function formatExcelData(rawData, columnMap, transformRow = null) {
    if (!rawData || rawData.length === 0) return [];

    return rawData.map(row => {
        let transformedRow = transformRow ? transformRow(row) : row;
        
        const formattedRow = {};
        Object.entries(columnMap).forEach(([apiKey, excelHeader]) => {
            formattedRow[excelHeader] = transformedRow[apiKey] ?? '';
        });
        
        return formattedRow;
    });
}

/**
 * Download CSV (alternative to Excel)
 * @param {Array} data - Array of objects
 * @param {string} filename - Filename without extension
 */
export function exportToCSV(data, filename) {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    try {
        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(data);
        
        // Convert to CSV
        const csv = XLSX.utils.sheet_to_csv(ws);
        
        // Create blob
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        
        const timestamp = moment(new Date()).format("yyyyMMDDHHmmss");
        FileSaver.saveAs(blob, `${filename}_${timestamp}.csv`);
    } catch (error) {
        console.error('Error exporting to CSV:', error);
        throw error;
    }
}

export default exportToExcel;
